import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Link2,
  Plus,
  Edit2,
  Trash2,
  Eye,
  EyeOff,
  Save,
  X,
  FileText,
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  Users,
  Calendar,
  Shield,
  Stethoscope
} from 'lucide-react';

const ManageLinks = () => {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('download');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    category_icon: 'FileText',
    category_color: '#10b981',
    online_link: '',
    download_link: '',
    form_type: 'both',
    is_visible: true,
    display_order: 0
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const iconOptions = [
    { value: 'Users', label: 'Users', icon: Users },
    { value: 'Calendar', label: 'Calendar', icon: Calendar },
    { value: 'Shield', label: 'Shield', icon: Shield },
    { value: 'FileText', label: 'File Text', icon: FileText },
    { value: 'Stethoscope', label: 'Stethoscope', icon: Stethoscope }
  ];

  const colorOptions = [
    { value: '#10b981', label: 'Green' },
    { value: '#3b82f6', label: 'Blue' },
    { value: '#8b5cf6', label: 'Purple' },
    { value: '#f59e0b', label: 'Amber' },
    { value: '#ef4444', label: 'Red' }
  ];

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/forms`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateForms = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/forms/migrate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        fetchForms();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error migrating forms' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleOpenModal = (form = null) => {
    if (form) {
      setEditingForm(form);
      setFormData({
        name: form.name,
        description: form.description,
        category: form.category,
        category_icon: form.category_icon,
        category_color: form.category_color,
        online_link: form.online_link || '',
        download_link: form.download_link || '',
        form_type: form.form_type,
        is_visible: form.is_visible,
        display_order: form.display_order
      });
    } else {
      setEditingForm(null);
      setFormData({
        name: '',
        description: '',
        category: '',
        category_icon: 'FileText',
        category_color: '#10b981',
        online_link: '',
        download_link: '',
        form_type: 'both',
        is_visible: true,
        display_order: forms.length + 1
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingForm(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      category_icon: 'FileText',
      category_color: '#10b981',
      online_link: '',
      download_link: '',
      form_type: 'both',
      is_visible: true,
      display_order: 0
    });
  };

  const handleSaveForm = async () => {
    try {
      const url = editingForm
        ? `${backendUrl}/api/forms/${editingForm.id}`
        : `${backendUrl}/api/forms`;
      
      const method = editingForm ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage({
          type: 'success',
          text: editingForm ? 'Form updated successfully' : 'Form created successfully'
        });
        fetchForms();
        handleCloseModal();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving form' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleDeleteClick = (form) => {
    setFormToDelete(form);
    setShowDeleteModal(true);
  };

  const handleDeleteForm = async () => {
    if (!formToDelete) return;

    try {
      const response = await fetch(`${backendUrl}/api/forms/${formToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Form deleted successfully' });
        fetchForms();
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error deleting form' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } finally {
      setShowDeleteModal(false);
      setFormToDelete(null);
    }
  };

  const handleToggleVisibility = async (form) => {
    try {
      const response = await fetch(`${backendUrl}/api/forms/${form.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_visible: !form.is_visible })
      });

      if (response.ok) {
        fetchForms();
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
    }
  };

  const getIconComponent = (iconName) => {
    const option = iconOptions.find(opt => opt.value === iconName);
    return option ? option.icon : FileText;
  };

  const filteredForms = forms.filter(form => {
    if (activeTab === 'download') return form.form_type === 'download' || form.form_type === 'both';
    if (activeTab === 'online') return form.form_type === 'online' || form.form_type === 'both';
    return true;
  });

  const groupedForms = filteredForms.reduce((acc, form) => {
    if (!acc[form.category]) {
      acc[form.category] = [];
    }
    acc[form.category].push(form);
    return acc;
  }, {});

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header - Compact Design like Customer Management */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                    Forms
                    <span className="text-xs font-normal text-gray-500">
                      Total: {forms.length} forms
                    </span>
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {/* Migrate Button (show if no forms) */}
                  {forms.length === 0 && (
                    <button
                      onClick={handleMigrateForms}
                      className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center justify-center"
                      style={{ backgroundColor: '#10b981' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                    >
                      <Upload className="h-4 w-4 mr-1.5" />
                      Migrate Forms
                    </button>
                  )}
                  
                  {/* Add New Form Button */}
                  <button
                    onClick={() => handleOpenModal()}
                    className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium flex items-center justify-center"
                    style={{ backgroundColor: '#f59e0b' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    Add New Form
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className={`text-sm ${message.type === 'error' ? 'text-red-800' : 'text-green-800'}`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex space-x-1 border-b border-gray-200 mb-6">
              <button
                onClick={() => setActiveTab('download')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'download'
                    ? 'text-theme-blue border-b-2 border-theme-blue'
                    : 'text-gray-600 hover:text-gray-900 hover:border-b-2 hover:border-gray-300'
                }`}
              >
                <Download className="inline h-4 w-4 mr-2" />
                Download Forms
              </button>
              <button
                onClick={() => setActiveTab('online')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'online'
                    ? 'text-theme-blue border-b-2 border-theme-blue'
                    : 'text-gray-600 hover:text-gray-900 hover:border-b-2 hover:border-gray-300'
                }`}
              >
                <FileText className="inline h-4 w-4 mr-2" />
                Online Forms
              </button>
            </div>

            {/* Forms List */}
            <div className="space-y-6">
              {Object.entries(groupedForms).map(([category, categoryForms]) => {
                const firstForm = categoryForms[0];
                const IconComponent = getIconComponent(firstForm.category_icon);
                
                return (
                  <div key={category} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center mb-4">
                      <div 
                        className="flex items-center justify-center w-10 h-10 rounded-full mr-3"
                        style={{ backgroundColor: `${firstForm.category_color}15` }}
                      >
                        <IconComponent className="h-5 w-5" style={{ color: firstForm.category_color }} />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900">{category}</h3>
                    </div>

                    <div className="space-y-3">
                      {categoryForms.map((form) => (
                        <div 
                          key={form.id}
                          className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="text-sm font-semibold text-gray-900">{form.name}</h4>
                                {!form.is_visible && (
                                  <span className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded">
                                    Hidden
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 mb-2">{form.description}</p>
                              <div className="flex flex-wrap gap-2 text-xs">
                                {form.online_link && (
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                    Online
                                  </span>
                                )}
                                {form.download_link && (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                    Download
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleToggleVisibility(form)}
                                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                                title={form.is_visible ? 'Hide' : 'Show'}
                              >
                                {form.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => handleOpenModal(form)}
                                className="p-1.5 text-theme-blue hover:text-blue-700 hover:bg-blue-50 rounded"
                                title="Edit"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(form)}
                                className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="flex items-center justify-between px-6 py-3 border-b sticky top-0 z-10" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <h2 className="text-lg font-semibold text-white">
                {editingForm ? 'Edit Form' : 'Add New Form'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 hover:opacity-80 transition-opacity"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-4">
                {/* Form Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Surgery & Anesthesia Consent Form"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                    placeholder="Brief description of the form"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Consent Forms, Patient Registration"
                  />
                </div>

                {/* Icon & Color */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Icon
                    </label>
                    <select
                      value={formData.category_icon}
                      onChange={(e) => setFormData({ ...formData, category_icon: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {iconOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category Color
                    </label>
                    <select
                      value={formData.category_color}
                      onChange={(e) => setFormData({ ...formData, category_color: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {colorOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Form Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Form Type *
                  </label>
                  <select
                    value={formData.form_type}
                    onChange={(e) => setFormData({ ...formData, form_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="both">Both (Online + Download)</option>
                    <option value="online">Online Only</option>
                    <option value="download">Download Only</option>
                  </select>
                </div>

                {/* Online Link */}
                {(formData.form_type === 'online' || formData.form_type === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Online Form Link
                    </label>
                    <input
                      type="text"
                      value={formData.online_link}
                      onChange={(e) => setFormData({ ...formData, online_link: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="/forms/surgery-consent or full URL"
                    />
                  </div>
                )}

                {/* Download Link */}
                {(formData.form_type === 'download' || formData.form_type === 'both') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Download PDF Link
                    </label>
                    <input
                      type="text"
                      value={formData.download_link}
                      onChange={(e) => setFormData({ ...formData, download_link: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="/documents/forms/filename.pdf"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use relative paths like: /documents/forms/Surgery_Consent_Form.pdf
                    </p>
                  </div>
                )}

                {/* Visibility & Display Order */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.is_visible}
                        onChange={(e) => setFormData({ ...formData, is_visible: e.target.checked })}
                        className="w-4 h-4 text-theme-blue border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">Visible to Users</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      value={formData.display_order}
                      onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="flex justify-end space-x-3 px-6 py-3 border-t bg-gray-50 sticky bottom-0">
              <button
                onClick={handleCloseModal}
                className="px-4 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveForm}
                className="inline-flex items-center px-4 py-1.5 text-sm text-white rounded-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                <Save className="h-4 w-4 mr-1.5" />
                {editingForm ? 'Update Form' : 'Create Form'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && formToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="px-5 py-3 border-b" style={{ backgroundColor: 'rgb(239, 68, 68)' }}>
              <h3 className="text-base font-semibold text-white flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Confirm Delete
              </h3>
            </div>
            
            {/* Content */}
            <div className="px-5 py-4">
              <p className="text-sm text-gray-700">
                Are you sure you want to delete <strong>"{formToDelete.name}"</strong>?
              </p>
              <p className="text-xs text-gray-500 mt-2">
                This action cannot be undone.
              </p>
            </div>
            
            {/* Footer */}
            <div className="flex justify-end space-x-3 px-5 py-3 bg-gray-50 border-t">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setFormToDelete(null);
                }}
                className="px-4 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteForm}
                className="inline-flex items-center px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageLinks;
